import type { ShopProduct } from '@/features/shop/types/shop.types'
import { stripSearchHighlightMarkup } from '@/shared/utilities/search-highlight-markup'
import { pickResultDisplay } from './search-result.model'
import { relevanceScore } from './search-ranking.model'
import type { SearchResult } from '../types/search.types'

/**
 * Where a typed query most likely wants to go when the person submits it
 * without picking a suggestion: the Shop's search, ProFinder, the business
 * directory, the hives list — or, when nothing is clear, the all-results page.
 */
export type SearchIntent = 'shop' | 'profinder' | 'business' | 'hive' | 'all'

export interface SearchIntentEvidence {
  hubRows: readonly SearchResult[]
  shopProducts: readonly ShopProduct[]
}

/** One dropdown row's say in the matter: the app it belongs to and how well it matches. */
export interface SearchIntentBallot {
  source: Exclude<SearchIntent, 'all'>
  title: string
  score: number
}

/**
 * `relevanceScore` levels: 1000 exact · 820 phrase · 720 every word · 650 inside
 * a word · 500 one word · 390/300 category · 140/80 description. A row votes
 * only from 720 up — every word of the query is a word (or the start of one)
 * in its name — so a recycling bin never speaks for "cycling".
 */
const VOTE_BAR = 720
const A_WORD = 500
/** As many rows as the dropdown shows. */
const MAX_VOTERS = 8
/** Ties are rare (sums of scores); when they happen, the more specific app wins. */
const TIE_ORDER: SearchIntentBallot['source'][] = ['profinder', 'shop', 'business', 'hive']

const str = (data: Record<string, unknown>, key: string) =>
  typeof data[key] === 'string' ? (data[key] as string) : undefined

function serviceNames(data: Record<string, unknown>): string {
  const value = data.service_funnel_names ?? data.services
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string'
          ? item
          : item && typeof item === 'object' && typeof (item as { name?: unknown }).name === 'string'
            ? ((item as { name: string }).name)
            : ''
      )
      .join(' ')
  }
  return typeof value === 'string' ? value : ''
}

/** Every row the dropdown is built from, scored against the query. */
export function scoreSearchCandidates(query: string, evidence: SearchIntentEvidence): SearchIntentBallot[] {
  const ballots: SearchIntentBallot[] = []
  for (const row of evidence.hubRows) {
    const display = pickResultDisplay(row)
    const title = stripSearchHighlightMarkup(display.title)
    const subtitle = display.subtitle ? stripSearchHighlightMarkup(display.subtitle) : undefined
    const data = row.data ?? {}
    switch (row.entity_type) {
      case 'service_funnel':
        ballots.push({ source: 'profinder', title, score: relevanceScore(query, { title, category: subtitle }) })
        break
      case 'business':
        ballots.push({
          source: 'business',
          title,
          score: relevanceScore(query, {
            title,
            secondary: serviceNames(data),
            description: str(data, 'summary') ?? str(data, 'description'),
          }),
        })
        break
      case 'hive':
        ballots.push({
          source: 'hive',
          title,
          score: relevanceScore(query, { title, description: str(data, 'description') }),
        })
        break
      default:
        break
    }
  }
  for (const product of evidence.shopProducts) {
    ballots.push({
      source: 'shop',
      title: product.title,
      score: relevanceScore(query, {
        title: product.title,
        category: product.variantLabel ?? product.variantFamilyName,
        secondary: product.business?.name,
        description: product.description,
      }),
    })
  }
  return ballots
}

/**
 * The dropdown votes. Every row that matches the query by whole words casts
 * its relevance score for the app it belongs to; the strongest eight count,
 * as many as the dropdown shows; the app with the most weight wins. "jerky"
 * is mostly Shop products, "plumbing" is mostly services, "Elden Foods" is
 * one exact business. Nothing votes → a service that shares a word still
 * means a pro; otherwise everything, on the all-results page.
 */
export function resolveSearchIntent(query: string, evidence: SearchIntentEvidence): SearchIntent {
  if (!query.trim()) return 'all'
  const ballots = scoreSearchCandidates(query, evidence)
  const voters = ballots
    .filter((ballot) => ballot.score >= VOTE_BAR)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_VOTERS)
  if (voters.length) {
    const tally = new Map<SearchIntentBallot['source'], number>()
    for (const voter of voters) tally.set(voter.source, (tally.get(voter.source) ?? 0) + voter.score)
    return [...tally.entries()].sort(
      (a, b) => b[1] - a[1] || TIE_ORDER.indexOf(a[0]) - TIE_ORDER.indexOf(b[0])
    )[0][0]
  }
  if (ballots.some((ballot) => ballot.source === 'profinder' && ballot.score >= A_WORD)) return 'profinder'
  return 'all'
}
